import {
  Button,
  FieldError,
  FormLabel,
  NewModal,
  TextInput,
} from '@pandiafi/design-system'
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

const linkSchema = z.string().url()
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const formValuesSchema = z.object({
  text: z.string(),
  link: linkSchema,
})

export type LinkFormModalValues = z.infer<typeof formValuesSchema>

interface LinkFormModalProps {
  show: boolean
  onSave: (values: LinkFormModalValues) => void
  onClose: () => void
  onDelete?: () => void
  value?: LinkFormModalValues
  edit?: boolean
}

const INITAL_VALUES: LinkFormModalValues = {
  text: '',
  link: '',
}

export const LinkFormModal: React.FC<LinkFormModalProps> = ({
  show,
  onClose,
  onSave,
  onDelete,
  value = INITAL_VALUES,
  edit = false,
}) => {
  const { t } = useTranslation(['common'])

  const handleValidate = (values: LinkFormModalValues) => {
    const errors: Partial<Record<keyof LinkFormModalValues, string>> = {}

    // Do not validate if modal is not visible
    if (!show) return errors

    if (!values.text) {
      errors.text = t('common:textEditor.textRequired')
    }
    if (!values.link) {
      errors.link = t('common:textEditor.linkRequired')
    }

    if (Object.keys(errors).length === 0) {
      if (!linkSchema.safeParse(values.link).success) {
        errors.link = t('common:textEditor.linkInvalid')
      }
    }

    return errors
  }

  const closeAndReset = (form: FormikHelpers<LinkFormModalValues>) => {
    form.setErrors({})
    form.resetForm()
    onClose()
  }

  const handleOnSubmit = (
    values: LinkFormModalValues,
    form: FormikHelpers<LinkFormModalValues>
  ) => {
    onSave(values)
    closeAndReset(form)
  }

  const handleOnCancel = (form: FormikHelpers<LinkFormModalValues>) => {
    closeAndReset(form)
  }

  const handleOnDelete = (form: FormikHelpers<LinkFormModalValues>) => {
    onDelete?.()
    closeAndReset(form)
  }

  return (
    <Formik
      initialValues={value}
      onSubmit={handleOnSubmit}
      validate={handleValidate}
      initialErrors={handleValidate(value)}
      enableReinitialize
      validateOnBlur
    >
      {(form) => (
        <NewModal
          header={false}
          show={show}
          onClose={() => handleOnCancel(form)}
        >
          <NewModal.Content>
            <Form>
              <div className="flex flex-col items-start">
                <FormLabel required for="text-input">
                  {t('common:textEditor.text')}
                </FormLabel>
                <Field name="text">
                  {({
                    field,
                    meta,
                  }: FieldProps<LinkFormModalValues['text']>) => (
                    <div className="mt-2 mb-4 w-full">
                      <TextInput {...field} id="text-input" />
                      {meta.touched && meta.error && (
                        <FieldError error={meta.error} />
                      )}
                    </div>
                  )}
                </Field>

                <FormLabel required for="link-input">
                  {t('common:textEditor.link')}
                </FormLabel>
                <Field name="link">
                  {({
                    field,
                    meta,
                  }: FieldProps<LinkFormModalValues['link']>) => (
                    <div className="mt-2 w-full">
                      <TextInput {...field} id="link-input" />
                      {meta.touched && meta.error && (
                        <FieldError error={meta.error} />
                      )}
                    </div>
                  )}
                </Field>
              </div>
            </Form>
          </NewModal.Content>
          <NewModal.Buttons
            leftContainer={
              edit && (
                <Button variant="delete" onClick={() => handleOnDelete(form)}>
                  {t('common:textEditor.removeLink')}
                </Button>
              )
            }
          >
            <Button variant="text" onClick={() => handleOnCancel(form)}>
              {t('common:cancel')}
            </Button>
            <Button
              onClick={() => form.submitForm()}
              disabled={!form.isValid || form.isSubmitting}
            >
              {t('common:save')}
            </Button>
          </NewModal.Buttons>
        </NewModal>
      )}
    </Formik>
  )
}
